import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {useBackground} from "../../../../hooks/background-class"
import iconCancel from "../../../../assets/images/icons/media/cancel.png"
import {ComplexButton} from "../AuthorizationGroupEdit"
import {useHistory} from "react-router-dom"
import {useSelector} from "react-redux"
import {onClickHandler} from "../../../../utils/onclick"
import {CameraBrandModel, ControlTypeModel, UserModel} from "../../../../types/control"
import {AuthorityModel, AuthorityOverviewList, AuthorityOverviewModel} from "../../../../store/control/state"
import {AreaModel, MenuEntity} from "../../../../store/control/state"
import {ControlModel, CameraModel} from "../../../../types/control"
import {useRPC} from "../../../../hooks/rpc"
import "./index.scss"
import {useLocalization} from "../../../../hooks/localization"

type AuthorityRelationType = AreaModel | MenuEntity | ControlModel | CameraModel
const selectList = <T extends AuthorityRelationType>(list: T[], selected: any[]) =>
    list.filter(value => selected?.map(r => r)?.includes(value?.id))

const findTypeById = <T extends CameraBrandModel | ControlTypeModel<unknown> | AreaModel>(list: T[], id: string) =>
    list.find(r => r.id === id)?.name

export const AuthorizationUserList = memo(function AuthorizationUserList() {
    const [, , backgroundStyle] = useBackground()
    const userList = useSelector(state => state.control.userList).filter(
        r => r.username !== "" && r.authority_group_id !== ""
    )
    const {t} = useLocalization()
    const authorityList = useSelector(state => state.control.authorityGroupList)
    const [selectedUser, setSelectedUser] = useState<{authority: AuthorityModel; user: UserModel}>(null)
    const history = useHistory()
    const areaList = useSelector(state => state.control.areaList)
    const controlTypeList = useSelector(state => state.control.controlTypeList)
    const [overviewList, setOverviewList] = useState<AuthorityOverviewList[]>([])
    const findList = useCallback((id: string): AuthorityOverviewModel => overviewList[id], [overviewList])
    const overviewListFiltered = findList(selectedUser?.authority?.id)
    const [rpc] = useRPC()

    const onClickSelectUser = useCallback(
        (selected: UserModel) => {
            const userIdList = authorityList.find(r => r.id === selected.authority_group_id)
            setSelectedUser({authority: userIdList, user: selected})
        },
        [authorityList]
    )

    const filteredSelectedUser = useMemo(() => {
        const selectDays = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]
        const selectedParameters = overviewListFiltered?.parameters?.day_list || []
        const days = {}
        selectDays.forEach(day => {
            days[day] = selectedParameters.includes(selectDays.indexOf(day))
        })

        return {
            id: selectedUser?.authority?.id,
            day: days,
            user_id: selectedUser?.user?.id,
            /*area_list: {
                executable: selectList(areaList, selectedUser?.authority?.area_list?.executable),
                viewable: selectList(areaList, selectedUser?.authority?.area_list?.viewable)
            },
            control_list: {
                executable: selectList(controlList, selectedUser?.authority?.control_id_list?.executable),
                viewable: selectList(controlList, selectedUser?.authority?.control_id_list?.viewable)
            },
            menu_list: selectList(menuList, selectedUser?.authority?.menu_list),
            camera_list: selectList(cameraList, selectedUser?.authority?.camera_list),*/
            auth_name: selectedUser?.authority?.name,
            username: selectedUser?.user?.username,
            after_time: selectedUser?.authority?.parameters?.after_time,
            before_time: selectedUser?.authority?.parameters?.before_time,
            min_temp: selectedUser?.authority?.parameters?.min_temp,
            max_temp: selectedUser?.authority?.parameters?.max_temp,
            is_notification: selectedUser?.authority?.parameters?.is_notification,
            can_disable_alarm: selectedUser?.authority?.parameters?.can_disable_alarm
        }
    }, [selectedUser, overviewListFiltered])

    const [controlRadioButton, setControlRadioButton] = useState(true)
    const [cameraRadioButton, setCameraRadioButton] = useState(false)
    const [leftMenuRadioButton, setLeftMenuRadioButton] = useState(false)
    const [viewableButton, setViewableButton] = useState(true)
    const [executableButton, setExecutableButton] = useState(false)

    const onClickChangeViewOption = useCallback((type: number) => {
        switch (type) {
            case 1:
                setControlRadioButton(true)
                setCameraRadioButton(false)
                setLeftMenuRadioButton(false)
                return
            case 2:
                setControlRadioButton(false)
                setCameraRadioButton(true)
                setLeftMenuRadioButton(false)
                return
            case 3:
                setControlRadioButton(false)
                setCameraRadioButton(false)
                setLeftMenuRadioButton(true)
                return
        }
    }, [])

    const onClickChangeAuthorityOption = useCallback((type: number) => {
        switch (type) {
            case 1:
                setViewableButton(true)
                setExecutableButton(false)
                return
            case 2:
                setViewableButton(false)
                setExecutableButton(true)
                return
        }
    }, [])

    useEffect(() => {
        rpc.getAuthorizationOverviewList().then(r => setOverviewList(r))
    }, [rpc])

    return (
        <PasswordContainer requiredRole="admin" isFullscreen={true}>
            <div className="auth-full-container" style={{backgroundImage: backgroundStyle?.backgroundImage}}>
                <div className="auth-user-list-container">
                    <div className="auth-user-list-field" style={{flex: 1}}>
                        <div className="auth-user-list">
                            <span>
                                {t("authority.user_list")}
                                <img src={iconCancel} alt="" onClick={() => history.push("/settings/authority")} />
                            </span>
                            <div className="auth-user-collection-filed">
                                {userList.map(user => (
                                    <div
                                        className={`auth-user-action-list ${
                                            filteredSelectedUser?.user_id === user.id && "__active"
                                        }`}
                                        key={user.id}
                                        {...onClickHandler(() => onClickSelectUser(user))}
                                    >
                                        {user.username}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="auth-user-list-field" style={{flex: 2}}>
                        {selectedUser != null && (
                            <div className="auth-user-list">
                                <div className="auth-user-top-details">
                                    <span>{filteredSelectedUser?.auth_name}</span>
                                </div>
                                <div className="auth-user-top-details">
                                    <ComplexButton
                                        isSelected={filteredSelectedUser.day["monday"]}
                                        title={t("authority.monday")}
                                        position="center"
                                        type="checkbox"
                                    />
                                    <ComplexButton
                                        isSelected={filteredSelectedUser.day["tuesday"]}
                                        title={t("authority.tuesday")}
                                        position="center"
                                        type="checkbox"
                                    />
                                    <ComplexButton
                                        isSelected={filteredSelectedUser.day["wednesday"]}
                                        title={t("authority.wednesday")}
                                        position="center"
                                        type="checkbox"
                                    />
                                    <ComplexButton
                                        isSelected={filteredSelectedUser.day["thursday"]}
                                        title={t("authority.thursday")}
                                        position="center"
                                        type="checkbox"
                                    />
                                    <ComplexButton
                                        isSelected={filteredSelectedUser.day["friday"]}
                                        title={t("authority.friday")}
                                        position="center"
                                        type="checkbox"
                                    />
                                    <ComplexButton
                                        isSelected={filteredSelectedUser.day["saturday"]}
                                        title={t("authority.saturday")}
                                        position="center"
                                        type="checkbox"
                                    />
                                    <ComplexButton
                                        isSelected={filteredSelectedUser.day["sunday"]}
                                        title={t("authority.sunday")}
                                        position="center"
                                        type="checkbox"
                                    />
                                </div>
                                <div className="auth-user-top-details">
                                    <div className="auth-text-details">
                                        <span>{t("authority.time_range")}</span>
                                        <div className="auth-text-status">
                                            <span>{t("authority.after")}</span>
                                            <span>{t("authority.before")}</span>
                                        </div>
                                        <p>
                                            <time>{filteredSelectedUser?.after_time || "--.--"}</time>|
                                            <time>{filteredSelectedUser?.before_time || "--.--"}</time>
                                        </p>
                                    </div>
                                    <div className="auth-text-details">
                                        <div className="auth-text-info">
                                            <ComplexButton
                                                isSelected={filteredSelectedUser?.is_notification}
                                                title={t("authority.notification_status")}
                                                position="center"
                                                type="radio"
                                            />
                                        </div>
                                    </div>
                                    <div className="auth-text-details">
                                        <div className="auth-text-info">
                                            <ComplexButton
                                                isSelected={filteredSelectedUser?.can_disable_alarm}
                                                title={t("authority.alarm_authority")}
                                                position="center"
                                                type="radio"
                                            />
                                        </div>
                                    </div>
                                    <div className="auth-text-details">
                                        <span>{t("authority.temperature_range")}</span>
                                        <div className="auth-text-status" style={{visibility: "hidden"}}>
                                            <span>{t("authority.after")}</span>
                                            <span>{t("authority.before")}</span>
                                        </div>
                                        <p>
                                            <time>{filteredSelectedUser?.min_temp + " °C" || "__.__"}</time>|
                                            <time>{filteredSelectedUser?.max_temp + " °C" || "__.__"}</time>
                                        </p>
                                    </div>
                                </div>
                                <div className="auth-user-top-details">
                                    <ComplexButton
                                        isSelected={controlRadioButton}
                                        title={t("authority.controls")}
                                        position="center"
                                        onClick={() => onClickChangeViewOption(1)}
                                        type="radio"
                                    />
                                    <ComplexButton
                                        isSelected={cameraRadioButton}
                                        title={t("authority.cameras")}
                                        position="center"
                                        onClick={() => onClickChangeViewOption(2)}
                                        type="radio"
                                    />
                                    <ComplexButton
                                        isSelected={leftMenuRadioButton}
                                        title={t("authority.left_menu")}
                                        position="center"
                                        onClick={() => onClickChangeViewOption(3)}
                                        type="radio"
                                    />
                                </div>
                                <div className="auth-user-top-details" style={{justifyContent: "center"}}>
                                    {renderIf(controlRadioButton)(() => (
                                        <>
                                            <ComplexButton
                                                isSelected={viewableButton}
                                                title={t("authority.can_see")}
                                                position="center"
                                                onClick={() => onClickChangeAuthorityOption(1)}
                                                type="radio"
                                            />
                                            <ComplexButton
                                                isSelected={executableButton}
                                                title={t("authority.can_run")}
                                                position="center"
                                                onClick={() => onClickChangeAuthorityOption(2)}
                                                type="radio"
                                            />
                                        </>
                                    ))}
                                </div>
                                <div className="auth-user-top-details">
                                    <div className="auth-action-list-container">
                                        {renderIf(controlRadioButton)(() =>
                                            renderIf(executableButton)(
                                                () =>
                                                    overviewListFiltered?.control_list?.executable.map(control => (
                                                        <div className="auth-result-list" key={control.id}>
                                                            <span>{control.name}</span>
                                                            <span>
                                                                <span>{t("authority.control_type")}</span>
                                                                <span>
                                                                    {findTypeById(controlTypeList, control?.type)}
                                                                </span>
                                                            </span>
                                                            <span>
                                                                <span>{t("authority.area_name")}</span>
                                                                <span>{findTypeById(areaList, control?.area_id)}</span>
                                                            </span>
                                                        </div>
                                                    )),
                                                () =>
                                                    overviewListFiltered?.control_list?.viewable.map(control => (
                                                        <div className="auth-result-list" key={control.id}>
                                                            <span>{control.name}</span>
                                                            <span>
                                                                <span>{t("authority.control_type")}</span>
                                                                <span>
                                                                    {findTypeById(controlTypeList, control?.type)}
                                                                </span>
                                                            </span>
                                                            <span>
                                                                <span>{t("authority.area_name")}</span>
                                                                <span>{findTypeById(areaList, control?.area_id)}</span>
                                                            </span>
                                                        </div>
                                                    ))
                                            )
                                        )}

                                        {renderIf(cameraRadioButton)(() =>
                                            overviewListFiltered?.camera_list?.map((camera, idx) => (
                                                <div className="auth-result-list" key={idx}>
                                                    <span>{camera}</span>
                                                    <span>
                                                        <span></span>
                                                        <span></span>
                                                    </span>
                                                </div>
                                            ))
                                        )}

                                        {renderIf(leftMenuRadioButton)(() =>
                                            overviewListFiltered?.menu_list?.map((menu, idx) => (
                                                <div className="auth-result-list" key={idx}>
                                                    <span>{menu}</span>
                                                    <span>
                                                        <span></span>
                                                        <span></span>
                                                    </span>
                                                    <span>
                                                        <span></span>
                                                        <span></span>
                                                    </span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PasswordContainer>
    )
})
